import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import Card from "components/card";
import "./SignIn.css";
import ErrorMessage from "./ErrorMessage";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { requestFirebaseToken } from "../../firebase";
import { trackPageVisit } from "../../utils/trackVisit";
import { readUser } from "utils/session";

export default function SignIn() {
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    trackPageVisit();
  }, []);

  useEffect(() => {
    if (readUser()?.token) {
      navigate("/admin");
    } else {
      navigate("/");
    }
  }, [navigate]);

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    let browser = "Unknown Browser";
    if (ua.includes("Firefox/")) browser = "Firefox";
    else if (ua.includes("Edg/")) browser = "Edge";
    else if (ua.includes("Chrome/")) browser = "Chrome";
    else if (ua.includes("Safari/") && !ua.includes("Chrome")) browser = "Safari";

    let os = "Unknown OS";
    if (ua.includes("Win")) os = "Windows";
    else if (ua.includes("Mac")) os = "MacOS";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("like Mac")) os = "iOS";

    // Returns a clean string like "Chrome on Windows"
    return `${browser} on ${os}`.substring(0, 100); 
  };

  const getDeviceTelemetry = () => {
    try {
      // 1. Basic OS/Browser
      const ua = navigator.userAgent || "";
      let browser = "Web";
      if (ua.includes("Firefox/")) browser = "Firefox";
      else if (ua.includes("Edg/")) browser = "Edge";
      else if (ua.includes("Chrome/")) browser = "Chrome";
      else if (ua.includes("Safari/") && !ua.includes("Chrome")) browser = "Safari";

      let os = "OS";
      if (ua.includes("Win")) os = "Win";
      else if (ua.includes("Mac")) os = "Mac";
      else if (ua.includes("Linux")) os = "Linux";
      else if (ua.includes("Android")) os = "Android";
      else if (ua.includes("like Mac")) os = "iOS";

      // 2. Screen Resolution (e.g., "1920x1080")
      const res = window.screen ? `${window.screen.width}x${window.screen.height}` : "";
      
      // 3. Timezone (e.g., "Asia/Kolkata") wrapped in its own try/catch just in case
      let tz = "";
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch(e){}

      // 4. Hardware (Cores & RAM) - Note: RAM is not supported on Safari/Firefox
      const cores = navigator.hardwareConcurrency ? `${navigator.hardwareConcurrency}C` : "";
      const ram = navigator.deviceMemory ? `${navigator.deviceMemory}GB` : "";
      const hardware = [cores, ram].filter(Boolean).join('/'); // Yields something like "8C/8GB"

      // 5. Assemble the string cleanly, omitting any empty values
      const parts = [`${browser} on ${os}`, res, tz, hardware].filter(Boolean);
      const finalString = parts.join(' | ');

      // 6. STRICT LIMIT: Ensure it never crashes the SQL VARCHAR(100) column
      return finalString.substring(0, 100);
      
    } catch (error) {
      // Graceful Exit: If anything goes wrong, just return a safe fallback. 
      // It will never stop the user from logging in.
      return "Unknown Web Device";
    }
  };

  const submitHandle = async (data) => {
    const { username, password } = data;

    try {
      const formData = new FormData();
      formData.append("username", username);
      formData.append("password", password);

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${apiBaseUrl}/api/login`,
        headers: {
          "X-Client-Type": "web-app",
        },
        data: formData,
      };

      const response = await axios.request(config);
      const userData = response.data;
      localStorage.setItem("userInfo", JSON.stringify(response.data));

      try {
        const fcmToken = await requestFirebaseToken();
        if (fcmToken) {
          localStorage.setItem("fcmToken", fcmToken);
          const fcmConfig = {
            method: "post",
            url: `${apiBaseUrl}/api/firebase/register-device`,
            headers: {
              "Content-Type": "application/json",
              // We must pass the newly acquired JWT to access this protected route
              "Authorization": `Bearer ${userData.token}` 
            },
            data: {
              user_id: userData.userid,
              fcm_token: fcmToken,
              device_type: "web", 
              // Calls our new silent telemetry function:
              device_model: getDeviceTelemetry(), 
              imei: null, 
              mac_address: null 
            },
          };
          await axios.request(fcmConfig);
          console.log("Device registered for push notifications.");
        }
      } catch (fcmError) {
        // We catch this separately so if FCM fails, it doesn't break the whole login process
        console.error("FCM Registration failed:", fcmError);
      }

      navigate("/admin");
    } catch (error) {
      if (error.response) {
        console.log("Error Data:", error.response.data);
        console.log("Error Status:", error.response.status);
        console.log("Error Headers:", error.response.headers);
      } else {
        console.log("Error Message:", error.message);
      }
      setError("Invalid Login Access!");
    }
  };

  return (
    <Card extra=" p-[48px] mb-6" style={{ width: "460px" }}>
      <div className="auth flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
        <div className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
          <h4 className="mb-6 text-center text-2xl font-bold text-blue-1000">
            hypertrace
          </h4>
          <p className="welcome-text text-dark mb-1 ml-1 text-base">
            Welcome to hypertrace! 👋🏻
          </p>
          <p className="mb-6 ml-1 text-base text-gray-600">
            Please sign-in to your account
          </p>
          <Box
            component="form"
            onSubmit={handleSubmit(submitHandle)}
            noValidate
          >
            {error && <ErrorMessage>{error}</ErrorMessage>}

            {/* Username*/}
            <TextField
              autoComplete="username"
              name="username"
              required
              fullWidth
              id="username"
              label="username"
              autoFocus
              variant="standard"
              {...register("username", {
                required: "username is required",
                minLength: { value: 2, message: "minimum length is 2" },
              })}
            />

            <p style={{ color: "red", fontSize: "12px" }}>
              {errors.username && errors.username.message}
            </p>

            {/* Password */}
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="password"
              variant="standard"
              {...register("password", {
                required: "Password is required",
              })}
            />

            <p style={{ color: "red", fontSize: "12px" }}>
              {errors.password && errors.password.message}
            </p>

            <button
              className="mt-2 w-full rounded-xl py-[12px] text-base font-medium bg-blue-1000 text-white transition duration-200"
              type="submit"
            >
              Sign In
            </button>
          </Box>
        </div>
      </div>
    </Card>
  );
}
